import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { toast } from 'react-hot-toast';
import { AePageHeader, AePageShell } from '@/components/assessment-engine/layout';
import { AE_CARD, AE_LINK } from '@/components/assessment-engine/layout/aeTheme';
import { assessmentEngineService } from '@/services/assessmentEngineService';
import type { AeTemplateSummary, CreateCampaignInput } from '@/types/assessmentEngine';
import { CampaignSetupForm, parseInviteEmailList } from './CampaignSetupForm';

/** Single-page form to deploy a new assessment campaign. */
export const CampaignDeployWizard: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<AeTemplateSummary[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    assessmentEngineService
      .listTemplates('published')
      .then((list) => setTemplates(list))
      .catch(() => toast.error(t('assessmentEngine.wizard.failedTemplates')));
  }, [t]);

  return (
    <AePageShell>
      <AePageHeader
        title={t('assessmentEngine.wizard.title')}
        description={t('assessmentEngine.wizard.singlePageDescription')}
        breadcrumbs={[
          { label: t('assessmentEngine.breadcrumbs.engine'), to: '/assessment-engine/campaigns' },
          { label: t('assessmentEngine.wizard.title') },
        ]}
      />

      <div className={AE_CARD}>
        <CampaignSetupForm
          mode="create"
          templates={templates}
          showInviteParticipantFields
          saving={saving}
          submitLabel={t('assessmentEngine.wizard.create')}
          footerStart={
            <Link to="/assessment-engine/campaigns" className={AE_LINK}>
              {t('assessmentEngine.campaign.cancel')}
            </Link>
          }
          onSubmit={async (payload, pendingInviteEmails) => {
            setSaving(true);
            try {
              const emails = parseInviteEmailList(pendingInviteEmails);
              const fullPayload = payload as CreateCampaignInput;
              const created = await assessmentEngineService.createCampaign(fullPayload);

              const reportDefaults = (fullPayload.report_defaults ?? {}) as Record<string, boolean | number>;
              await assessmentEngineService
                .updateReportConfig(created.id, {
                  show_instant_individual_results: reportDefaults.show_instant_individual_results !== false,
                  benchmark_b1_min_responses: Math.max(
                    1,
                    Number(reportDefaults.benchmark_b1_min_responses ?? 10)
                  ),
                })
                .catch(() => {});

              if (emails.length > 0) {
                const inviteResult = await assessmentEngineService.bulkInvites(created.id, emails);
                if (inviteResult.emails_deferred) {
                  toast.success(t('assessmentEngine.wizard.invitesAddedPendingOpen', { count: inviteResult.inserted }));
                }
              }

              toast.success(t('assessmentEngine.wizard.created'));
              navigate(`/assessment-engine/campaigns/${created.id}?tab=invitations`);
            } catch {
              toast.error(t('assessmentEngine.wizard.createFailed'));
            } finally {
              setSaving(false);
            }
          }}
        />
      </div>
    </AePageShell>
  );
};
